import { ButtonGradientPrimary, Colors, Icon, Text } from '@amelio/ui-library';

import { AgendaOpeningStatementHint } from './AgendaOpeningStatementHint';
import { AgendaOpeningStatementTitle } from './AgendaOpeningStatementTitle';
import { AGENDA_OPENING_STATEMENT_MAX_LENGTH } from '../agendaLimits';

import { useAmelioTranslations } from '@/hooks';

interface AgendaOpeningStatementProps {
    value: string;
    isEditing: boolean;
    /** Read-only agenda (a completed meeting): the statement is shown, with no pencil to offer an
     * edit that could not happen. @default false */
    disabled?: boolean;
    onChange: (value: string) => void;
    onEdit: () => void;
    onBlur: () => void;
}

export const AgendaOpeningStatement = ({
    value,
    isEditing,
    disabled = false,
    onChange,
    onEdit,
    onBlur,
}: AgendaOpeningStatementProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'mt-6'}>
            <div className={'mb-4 flex items-center gap-4'}>
                <AgendaOpeningStatementTitle />

                {disabled ? null : (
                    <button
                        type={'button'}
                        onClick={onEdit}
                        // Carried no sizing at all, so its target was whatever the icon happened to
                        // measure — 21 x 22 px on screen, below the 24 px minimum every interactive
                        // surface owes. The box belongs to the button, not to the glyph inside it.
                        className={'inline-flex h-8 w-8 items-center justify-center rounded'}
                        aria-label={t('meetingAgenda.editOpeningStatement')}
                        data-testid={'edit-opening-statement-button'}
                    >
                        <Icon
                            name={'edit-edit-2'}
                            size={'lg'}
                            color={Colors.BASE_400}
                        />
                    </button>
                )}
            </div>

            {isEditing && !disabled ? (
                <>
                    <div className={'pl-6'}>
                        <textarea
                            autoFocus
                            value={value}
                            onChange={(event) => onChange(event.target.value)}
                            onBlur={(event) => {
                                if (
                                    event.relatedTarget?.getAttribute('data-testid') !==
                                    'opening-statement-save-button'
                                ) {
                                    onBlur();
                                }
                            }}
                            className={
                                'border-base-200 focus:border-primary text-md w-full rounded-lg border bg-white px-4 py-3 leading-relaxed transition-colors duration-200 focus:outline-none'
                            }
                            rows={3}
                            maxLength={AGENDA_OPENING_STATEMENT_MAX_LENGTH}
                            data-testid={'opening-statement-textarea'}
                        />
                    </div>

                    <div className={'mt-2 flex justify-end'}>
                        <ButtonGradientPrimary
                            size={'lg'}
                            fitContent
                            shape={'pill'}
                            leftIcon={
                                <Icon
                                    name={'essential-checkmark'}
                                    size={'lg'}
                                    color={Colors.WHITE}
                                />
                            }
                            onClick={onBlur}
                            dataTestId={'opening-statement-save-button'}
                            className={
                                '!bg-primary !h-auto !border-none !px-5 !py-2 shadow-md transition-all duration-200 hover:!scale-105 hover:!shadow-lg'
                            }
                        >
                            {t('common.save')}
                        </ButtonGradientPrimary>
                    </div>
                </>
            ) : (
                <Text
                    size={'default'}
                    color={Colors.BASE_400}
                    className={'mt-2 pl-6'}
                >
                    {value}
                </Text>
            )}

            {!disabled && <AgendaOpeningStatementHint />}
        </div>
    );
};

export default AgendaOpeningStatement;
